import { Box, Button, Group, Paper, Stack, Text } from "@mantine/core";
import { IconArrowRight } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import CountryFlag from "@/components/shared/CountryFlag";
import { getCountryDisplayLabelFromHeadquarters } from "../../../../../../shared/worldCountryIsoCodes";
import { industryRouteParam, type EcosystemHeadNavState } from "@/pages/ecosystem-heads/ecosystemHeads.const";
import type { SectorEcosystemCorridorHead } from "../../../../../../shared/sectorEcosystemOverview";

const CARD_BLUE = "#2563EB";
const CARD_BLUE_HOVER = "#1D4ED8";
const CARD_BORDER = "#BFDBFE";

function formatCorridorLabel(country: string, targetCountry: string): string {
  const from = (getCountryDisplayLabelFromHeadquarters(country) ?? country).toUpperCase();
  const to = (getCountryDisplayLabelFromHeadquarters(targetCountry) ?? targetCountry).toUpperCase();
  return `${from} ↔ ${to}`;
}

type SectorEcosystemCorridorCardProps = {
  corridor: SectorEcosystemCorridorHead;
  navState?: EcosystemHeadNavState;
};

function FlagBadge({ country }: { country: string }) {
  return (
    <Box
      style={{
        padding: 8,
        borderRadius: 12,
        backgroundColor: "#FFFFFF",
        border: `1px solid ${CARD_BORDER}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box style={{ transform: "scale(1.4)", transformOrigin: "center", lineHeight: 0 }}>
        <CountryFlag country={country} />
      </Box>
    </Box>
  );
}

export default function SectorEcosystemCorridorCard({ corridor, navState }: SectorEcosystemCorridorCardProps) {
  const headHref = `/ecosystem-heads/${industryRouteParam(corridor.industry)}/${corridor.ecosystemHeadUserId}`;

  return (
    <Paper
      radius="lg"
      shadow="none"
      h="100%"
      mih={188}
      style={{
        border: `1px solid ${CARD_BORDER}`,
        backgroundColor: "#FFFFFF",
        overflow: "hidden",
      }}
    >
      <Stack gap="md" h="100%" p="lg" justify="space-between">
        <Stack gap="md" align="center">
          <Group gap="sm" justify="center" wrap="nowrap">
            <FlagBadge country={corridor.country} />
            <Text fw={700} size="lg" c={CARD_BLUE} lh={1}>
              ↔
            </Text>
            <FlagBadge country={corridor.targetCountry} />
          </Group>

          <Text fw={700} size="sm" tt="uppercase" ta="center" c="#04305c" lineClamp={2} lh={1.35}>
            {formatCorridorLabel(corridor.country, corridor.targetCountry)}
          </Text>
        </Stack>

        <Button
          component={Link}
          to={headHref}
          state={navState}
          size="sm"
          fullWidth
          rightSection={<IconArrowRight size={14} />}
          styles={{
            root: {
              backgroundColor: CARD_BLUE,
              minHeight: 38,
              fontWeight: 600,
              "&:hover": { backgroundColor: CARD_BLUE_HOVER },
            },
          }}
        >
          View Ecosystem
        </Button>
      </Stack>
    </Paper>
  );
}
